import { useState } from "react";
import { Plus, Trash2, Tag } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useSession } from "@/context/SessionContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const blank = { label: "", english_form_id: "", spanish_form_id: "" };

export const FormsTab = ({ site, onChange }) => {
  const { session } = useSession();
  const [row, setRow] = useState(blank);

  const save = async (form_map) => {
    try {
      await api.patch(`/sites/${site.id}`, { form_map });
      onChange?.();
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };

  const add = async (e) => {
    e.preventDefault();
    const next = [...site.form_map, { ...row, id: `f_${Date.now()}` }];
    if (await save(next)) { setRow(blank); toast.success("Spanish form mapped"); }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="forms-tab">
      <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 text-blue-900 rounded-lg p-4 text-sm">
        <Tag className="h-4 w-4 mt-0.5 shrink-0" />
        <span>On Spanish pages, each English form or calendar embed is swapped for the Spanish one you pick. In each Spanish form's GHL settings, add the tag <strong>Spanish</strong>, so every lead from Spanish pages carries it.</span>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="form-map-list">
        {site.form_map.length === 0 && <div className="p-6 text-sm text-slate-500" data-testid="form-map-empty">No forms mapped yet.</div>}
        {site.form_map.map((f) => (
          <div key={f.id} className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_1fr_1fr_auto] gap-3 items-center p-4 text-sm" data-testid={`form-map-row-${f.id}`}>
            <span className="font-medium">{f.label || "Form"}</span>
            <span className="font-mono text-xs text-slate-500 hidden sm:block">EN {f.english_form_id}</span>
            <span className="font-mono text-xs text-slate-900 hidden sm:block">ES {f.spanish_form_id}</span>
            <Button variant="ghost" size="icon" disabled={!session.can_edit} data-testid={`form-map-delete-${f.id}`} onClick={() => save(site.form_map.filter((x) => x.id !== f.id))}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      <form onSubmit={add} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
        <Input placeholder="Label (Quote form)" value={row.label} onChange={(e) => setRow({ ...row, label: e.target.value })} data-testid="form-label-input" />
        <Input placeholder="English form/calendar ID" className="font-mono" required value={row.english_form_id} onChange={(e) => setRow({ ...row, english_form_id: e.target.value })} data-testid="form-english-id-input" />
        <Input placeholder="Spanish form/calendar ID" className="font-mono" required value={row.spanish_form_id} onChange={(e) => setRow({ ...row, spanish_form_id: e.target.value })} data-testid="form-spanish-id-input" />
        <Button type="submit" disabled={!session.can_edit} data-testid="form-map-add-button"><Plus className="h-4 w-4 mr-1" /> Map</Button>
      </form>
    </div>
  );
};
